<template>
  <div class="app-container quote-page" :class="{ 'quote-page--embedded': embedded }">
    <template v-if="!embedded">
      <el-form ref="queryForm" :model="queryParams" class="multipleSelectScroll quote-query" v-show="showSearch" label-width="86px">
        <el-row :gutter="10" class="mb8">
          <el-col :xs="24" :md="12" :lg="6">
            <el-form-item label="报价搜索" prop="keyword">
              <el-input v-model="queryParams.keyword" placeholder="编号/名称/客户" clearable @keyup.enter.native="handleQuery" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12" :lg="6">
            <el-form-item label="报价状态" prop="status">
              <el-select v-model="queryParams.status" placeholder="请选择状态" clearable style="width:100%">
                <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12" :lg="6">
            <el-form-item label="风险状态" prop="riskFlag">
              <el-select v-model="queryParams.riskFlag" placeholder="请选择风险" clearable style="width:100%">
                <el-option label="无风险" :value="0" />
                <el-option label="有风险" :value="1" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12" :lg="6" align="right">
            <el-form-item>
              <el-button type="primary" icon="el-icon-search" @click="handleQuery">搜索</el-button>
              <el-button icon="el-icon-refresh" @click="resetQuery">清空</el-button>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <el-row :gutter="10" class="mb8">
        <el-col :span="1.5">
          <el-button v-hasPermi="['crm:quote:save']" type="primary" icon="el-icon-plus" @click="handleAdd">新建报价</el-button>
        </el-col>
        <right-toolbar :showSearch.sync="showSearch" @queryTable="getList"></right-toolbar>
      </el-row>

      <el-row class="total-count">
        <span>报价单：{{ summary.total }}</span>
        <span>报价总额：{{ money(summary.totalAmount) }}</span>
        <span>预计成本：{{ money(summary.costAmount) }}</span>
        <span>平均毛利率：{{ summary.avgMargin }}%</span>
        <span>风险单：{{ summary.riskCount }}</span>
      </el-row>

      <el-table v-loading="loading" :data="dataList" height="calc(100vh - 320px)" @row-dblclick="handleView">
        <el-table-column label="报价编号" width="230" fixed="left" show-overflow-tooltip>
          <template slot-scope="scope">
            <span class="quote-no-cell">
              <el-link
                type="primary"
                class="quote-no-link"
                :underline="false"
                :title="scope.row.quoteNo || '-'"
                @click="handleView(scope.row)"
              >{{ scope.row.quoteNo }}</el-link>
              <el-button
                v-if="scope.row.quoteNo"
                type="text"
                icon="el-icon-document-copy"
                title="复制报价编号"
                @click.stop="copyText(scope.row.quoteNo, '报价编号')"
              />
            </span>
          </template>
        </el-table-column>
        <el-table-column label="报价名称" prop="quoteName" min-width="180" fixed="left" show-overflow-tooltip />
        <el-table-column label="客户" prop="customerName" min-width="160" show-overflow-tooltip />
        <el-table-column label="商机/项目" prop="businessName" min-width="160" show-overflow-tooltip />
        <el-table-column label="报价金额" width="120" align="right">
          <template slot-scope="scope">{{ money(scope.row.totalAmount) }}</template>
        </el-table-column>
        <el-table-column label="预计成本" width="120" align="right">
          <template slot-scope="scope">{{ money(scope.row.costAmount) }}</template>
        </el-table-column>
        <el-table-column label="毛利率" width="90" align="right">
          <template slot-scope="scope">{{ numberText(scope.row.grossMargin) }}%</template>
        </el-table-column>
        <el-table-column label="风险" width="110" align="center">
          <template slot-scope="scope">
            <el-tag v-if="Number(scope.row.riskFlag) === 1" type="danger" size="mini">{{ riskText(scope.row.riskSummary) }}</el-tag>
            <el-tag v-else size="mini">无风险</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="statusTag(scope.row.status)" size="mini">{{ statusText(scope.row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="用章审批" width="110" align="center">
          <template slot-scope="scope">
            <el-tag :type="sealStatusTag(scope.row.sealStatus)" size="mini">{{ sealStatusText(scope.row.sealStatus) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="关联合同" width="100" align="center">
          <template slot-scope="scope">{{ scope.row.contractId || '-' }}</template>
        </el-table-column>
        <el-table-column label="更新时间" prop="updateTime" width="160" align="center" />
        <el-table-column label="操作" width="430" fixed="right" align="center" class-name="small-padding fixed-width">
          <template slot-scope="scope">
            <el-button type="text" @click="handleView(scope.row)">查看</el-button>
            <el-button type="text" @click="handleQuoteSheet(scope.row)">报价单</el-button>
            <el-button type="text" @click="exportContractDraft(scope.row)">合同草稿</el-button>
            <el-button v-if="canEditQuote(scope.row)" v-hasPermi="['crm:quote:save']" type="text" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button v-if="canSubmitSealApproval(scope.row)" v-hasPermi="['crm:quote:sealApproval']" type="text" @click="openSealApproval(scope.row)">发起用章</el-button>
            <el-button v-if="scope.row.sealWorkOrderId" v-hasPermi="['crm:quote:approvalRecord']" type="text" @click="openApprovalRecord(scope.row)">审批记录</el-button>
            <el-button v-hasPermi="['crm:quote:convertContract']" type="text" @click="handleCreateContract(scope.row)">{{ scope.row.contractId ? '查看合同' : '生成合同' }}</el-button>
          </template>
        </el-table-column>
      </el-table>
    </template>

    <el-dialog :title="formTitle" :visible.sync="formOpen" width="1240px" append-to-body :close-on-click-modal="false" class="quote-dialog">
      <el-form ref="quoteForm" :model="form" :rules="rules" label-width="92px">
        <el-divider content-position="left">基本信息</el-divider>
        <el-row :gutter="12">
          <el-col :xs="24" :md="12" :lg="8">
            <el-form-item label="报价名称" prop="quoteName">
              <el-input v-model="form.quoteName" maxlength="120" placeholder="请输入报价名称" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12" :lg="8">
            <el-form-item label="客户名称" prop="customerName">
              <el-input v-model="form.customerName" maxlength="120" placeholder="请输入客户名称" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12" :lg="8">
            <el-form-item label="商机/项目">
              <el-input v-model="form.businessName" maxlength="120" placeholder="请输入商机或项目名称" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12" :lg="8">
            <el-form-item label="联系人">
              <el-input v-model="form.contactName" maxlength="80" placeholder="请输入联系人" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12" :lg="8">
            <el-form-item label="联系电话">
              <el-input v-model="form.contactMobile" maxlength="40" placeholder="请输入联系电话" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12" :lg="8">
            <el-form-item label="报价日期">
              <el-date-picker v-model="form.quoteDate" value-format="yyyy-MM-dd" type="date" placeholder="请选择报价日期" style="width:100%" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12" :lg="8">
            <el-form-item label="有效期至">
              <el-date-picker v-model="form.validUntil" value-format="yyyy-MM-dd" type="date" placeholder="请选择有效期" style="width:100%" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :md="12" :lg="8">
            <el-form-item label="优惠金额">
              <el-input-number v-model="form.discountAmount" :min="0" :precision="2" :step="100" style="width:100%" @change="recalculateLocal" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider content-position="left">商品明细</el-divider>
        <div class="table-actions">
          <el-button type="primary" icon="el-icon-plus" size="mini" @click="openProductSelector">选择商品</el-button>
          <el-select
            v-model="selectedTemplateCode"
            size="mini"
            filterable
            placeholder="选择报价模板"
            class="quote-template-select"
            :loading="templateLoading"
            @visible-change="handleTemplateVisible"
          >
            <el-option
              v-for="item in templateList"
              :key="item.templateCode"
              :label="item.templateName"
              :value="item.templateCode"
            >
              <span>{{ item.templateName }}</span>
              <span class="quote-template-option">{{ quoteTypeLabel(item.quoteType) }}</span>
            </el-option>
          </el-select>
          <el-button icon="el-icon-s-grid" size="mini" :loading="templateLoading" :disabled="!selectedTemplateCode" @click="loadSelectedTemplate">载入模板</el-button>
          <el-button icon="el-icon-edit" size="mini" @click="addNonStandard">新增非标项</el-button>
          <el-button icon="el-icon-data-analysis" size="mini" @click="calculateRemote(false)">测算金额</el-button>
        </div>
        <el-table :data="form.itemList" border class="quote-item-table">
          <el-table-column label="商品" min-width="210" fixed="left" show-overflow-tooltip>
            <template slot-scope="scope">
              <div class="product-cell">
                <strong>{{ scope.row.productName || '非标项' }}</strong>
                <span>{{ scope.row.productCode || '未关联标准商品' }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="型号" prop="productModel" min-width="130">
            <template slot-scope="scope"><el-input v-model="scope.row.productModel" size="mini" /></template>
          </el-table-column>
          <el-table-column label="单位" width="80">
            <template slot-scope="scope"><el-input v-model="scope.row.saleUnit" size="mini" /></template>
          </el-table-column>
          <el-table-column label="数量" width="120" align="right">
            <template slot-scope="scope">
              <el-input-number v-model="scope.row.quantity" :min="0" :precision="2" size="mini" controls-position="right" @change="recalculateLocal" />
            </template>
          </el-table-column>
          <el-table-column label="标准价" width="110" align="right">
            <template slot-scope="scope">{{ money(scope.row.standardPrice) }}</template>
          </el-table-column>
          <el-table-column label="保底价" width="110" align="right">
            <template slot-scope="scope">{{ money(scope.row.floorPrice) }}</template>
          </el-table-column>
          <el-table-column label="销售单价" width="130" align="right">
            <template slot-scope="scope">
              <el-input-number v-model="scope.row.salePrice" :min="0" :precision="2" size="mini" controls-position="right" @change="recalculateLocal" />
            </template>
          </el-table-column>
          <el-table-column label="折扣%" width="110" align="right">
            <template slot-scope="scope">
              <el-input-number v-model="scope.row.discountRate" :min="0" :max="100" :precision="2" size="mini" controls-position="right" @change="recalculateLocal" />
            </template>
          </el-table-column>
          <el-table-column label="金额" width="120" align="right">
            <template slot-scope="scope">{{ money(scope.row.amount) }}</template>
          </el-table-column>
          <el-table-column label="预计成本" width="135" align="right">
            <template slot-scope="scope">
              <el-input-number v-model="scope.row.estimatedCost" :min="0" :precision="2" size="mini" controls-position="right" @change="recalculateLocal" />
            </template>
          </el-table-column>
          <el-table-column label="毛利率" width="90" align="right">
            <template slot-scope="scope"><span :class="{ danger: hasRisk(scope.row, 'LOW_MARGIN') }">{{ numberText(scope.row.grossMargin) }}%</span></template>
          </el-table-column>
          <el-table-column label="风险" width="160" align="center">
            <template slot-scope="scope">
              <el-tag v-for="tag in riskTags(scope.row.riskFlags)" :key="tag" :type="riskTagType(tag)" size="mini" class="risk-tag">{{ riskLabel(tag) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="报价规则" min-width="220" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.pricingRuleSnapshot || '-' }}</template>
          </el-table-column>
          <el-table-column label="备注" min-width="160">
            <template slot-scope="scope"><el-input v-model="scope.row.remark" size="mini" /></template>
          </el-table-column>
          <el-table-column label="操作" width="70" fixed="right" align="center">
            <template slot-scope="scope">
              <el-button type="text" class="danger" @click="removeItem(scope.$index)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div class="summary-panel">
          <span>报价总额：<strong>{{ money(form.totalAmount) }}</strong></span>
          <span>预计成本：<strong>{{ money(form.costAmount) }}</strong></span>
          <span>预计毛利：<strong>{{ money(form.grossProfit) }}</strong></span>
          <span>预计毛利率：<strong>{{ numberText(form.grossMargin) }}%</strong></span>
          <span v-if="form.riskSummary">风险：<strong class="danger">{{ riskText(form.riskSummary) }}</strong></span>
        </div>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="formOpen = false">取消</el-button>
        <el-button icon="el-icon-data-analysis" :loading="calculateLoading" @click="calculateRemote(false)">测算金额</el-button>
        <el-button :loading="submitLoading" @click="submitForm(false)">仅保存</el-button>
        <el-button type="primary" :loading="submitLoading" @click="submitForm(true)">保存并生成报价单</el-button>
      </div>
    </el-dialog>

    <el-dialog title="选择标准商品" :visible.sync="productSelectorOpen" width="1060px" append-to-body>
      <el-form :model="productQuery" label-width="80px" class="product-selector-query">
        <el-row :gutter="10">
          <el-col :span="10">
            <el-form-item label="商品搜索">
              <el-input v-model="productQuery.keyword" placeholder="编码/名称/型号" clearable @keyup.enter.native="getProductList" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="商品品类">
              <el-input v-model="productQuery.categoryName" placeholder="请输入品类" clearable @keyup.enter.native="getProductList" />
            </el-form-item>
          </el-col>
          <el-col :span="6" align="right">
            <el-button type="primary" icon="el-icon-search" @click="getProductList">搜索</el-button>
          </el-col>
        </el-row>
      </el-form>
      <el-table v-loading="productLoading" :data="productList" height="430px" @row-dblclick="selectProduct">
        <el-table-column label="商品编码" prop="productCode" min-width="170" show-overflow-tooltip />
        <el-table-column label="商品名称" prop="productName" min-width="180" show-overflow-tooltip />
        <el-table-column label="品类" prop="categoryName" width="100" />
        <el-table-column label="型号" prop="productModel" min-width="130" show-overflow-tooltip />
        <el-table-column label="单位" prop="saleUnit" width="70" align="center" />
        <el-table-column label="标准价" width="100" align="right">
          <template slot-scope="scope">{{ money(scope.row.standardPrice) }}</template>
        </el-table-column>
        <el-table-column label="成本" width="100" align="right">
          <template slot-scope="scope">{{ money(scope.row.standardCost) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="90" align="center">
          <template slot-scope="scope"><el-tag :type="productStatusTag(scope.row.status)" size="mini">{{ productStatusText(scope.row.status) }}</el-tag></template>
        </el-table-column>
        <el-table-column label="操作" width="80" align="center">
          <template slot-scope="scope"><el-button type="text" @click="selectProduct(scope.row)">选择</el-button></template>
        </el-table-column>
      </el-table>
    </el-dialog>

    <el-dialog title="报价单" :visible.sync="quoteSheetOpen" width="1120px" append-to-body class="quote-sheet-dialog">
      <div class="quote-sheet-actions">
        <el-button type="primary" icon="el-icon-printer" :disabled="quoteSheetLoading" @click="printQuoteSheet">打印/另存PDF</el-button>
        <el-button icon="el-icon-download" :disabled="quoteSheetLoading || !quoteSheetItems.length" @click="exportQuoteExcel">导出Excel报价单</el-button>
      </div>
      <div v-loading="quoteSheetLoading" class="quote-sheet-scroll">
        <div ref="quoteSheetPrint" class="quote-sheet-document">
          <div class="quote-sheet-header">
            <div>
              <h1>瑞洁特产品报价单</h1>
              <p>{{ quoteSheetQuote.quoteName || '-' }}</p>
            </div>
            <div class="quote-sheet-no">
              <span>报价编号</span>
              <strong>{{ quoteSheetQuote.quoteNo || '-' }}</strong>
            </div>
          </div>

          <div class="quote-sheet-meta">
            <div><span>客户名称</span><strong>{{ quoteSheetQuote.customerName || '-' }}</strong></div>
            <div><span>项目/商机</span><strong>{{ quoteSheetQuote.businessName || '-' }}</strong></div>
            <div><span>联系人</span><strong>{{ quoteSheetQuote.contactName || '-' }}</strong></div>
            <div><span>联系电话</span><strong>{{ quoteSheetQuote.contactMobile || '-' }}</strong></div>
            <div><span>报价日期</span><strong>{{ quoteDisplayDate(quoteSheetQuote.quoteDate) }}</strong></div>
            <div><span>有效期至</span><strong>{{ quoteSheetQuote.validUntil || '-' }}</strong></div>
          </div>

          <table v-if="isEquipmentQuoteSheet" class="quote-sheet-table quote-sheet-table--equipment">
            <thead>
              <tr>
                <th>序号</th>
                <th>名称</th>
                <th>内容/规格</th>
                <th>数量</th>
                <th>单位</th>
                <th>单价</th>
                <th>总价</th>
                <th>服务类型</th>
                <th>备注</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(group, groupIndex) in quoteSheetEquipmentGroups">
                <tr :key="`group-${group.name}`" class="equipment-group-row">
                  <td class="center">{{ equipmentGroupLabel(groupIndex) }}</td>
                  <td colspan="8"><strong>{{ group.name }}</strong></td>
                </tr>
                <tr v-for="item in group.items" :key="item.id || `${group.name}-${item.lineNo}`">
                  <td class="center">{{ item.lineNo || '-' }}</td>
                  <td>{{ item.productName || '非标项' }}</td>
                  <td>{{ item.productModel || '-' }}</td>
                  <td class="number">{{ numberText(item.quantity) }}</td>
                  <td class="center">{{ item.saleUnit || '-' }}</td>
                  <td class="number">{{ money(item.salePrice) }}</td>
                  <td class="number">{{ money(item.amount) }}</td>
                  <td>{{ item.deliveryScopeSnapshot || '-' }}</td>
                  <td>{{ item.remark || '-' }}</td>
                </tr>
                <tr :key="`subtotal-${group.name}`" class="equipment-subtotal-row">
                  <td></td>
                  <td>小计</td>
                  <td colspan="4"></td>
                  <td class="number">{{ money(group.subtotal) }}</td>
                  <td colspan="2"></td>
                </tr>
              </template>
              <tr v-if="quoteSheetFeeItems.length" class="equipment-group-row">
                <td></td>
                <td colspan="8"><strong>合价</strong></td>
              </tr>
              <tr v-for="(item, index) in quoteSheetFeeItems" :key="item.id || `fee-${index}`">
                <td class="center">{{ index + 1 }}</td>
                <td>{{ item.productName || '费用项' }}</td>
                <td>{{ feeDisplayRule(item) }}</td>
                <td class="number">{{ numberText(item.quantity) }}</td>
                <td class="center">{{ item.saleUnit || '-' }}</td>
                <td class="number">{{ money(item.salePrice) }}</td>
                <td class="number">{{ money(item.amount) }}</td>
                <td>{{ item.deliveryScopeSnapshot || '-' }}</td>
                <td>{{ item.remark || '-' }}</td>
              </tr>
              <tr v-if="!quoteSheetItems.length">
                <td colspan="9" class="center empty">暂无报价明细</td>
              </tr>
            </tbody>
          </table>

          <table v-else class="quote-sheet-table">
            <thead>
              <tr>
                <th>序号</th>
                <th>商品/服务</th>
                <th>型号规格</th>
                <th>单位</th>
                <th>数量</th>
                <th>销售单价</th>
                <th>折扣</th>
                <th>金额</th>
                <th>报价说明</th>
                <th>备注</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in quoteSheetItems" :key="item.id || index">
                <td class="center">{{ index + 1 }}</td>
                <td>
                  <strong>{{ item.productName || '非标项' }}</strong>
                  <small v-if="item.productCode">{{ item.productCode }}</small>
                </td>
                <td>{{ item.productModel || '-' }}</td>
                <td class="center">{{ item.saleUnit || '-' }}</td>
                <td class="number">{{ numberText(item.quantity) }}</td>
                <td class="number">{{ money(item.salePrice) }}</td>
                <td class="number">{{ numberText(item.discountRate == null ? 100 : item.discountRate) }}%</td>
                <td class="number">{{ money(item.amount) }}</td>
                <td>{{ item.pricingRuleSnapshot || item.deliveryScopeSnapshot || '-' }}</td>
                <td>{{ item.remark || '-' }}</td>
              </tr>
              <tr v-if="!quoteSheetItems.length">
                <td colspan="10" class="center empty">暂无报价明细</td>
              </tr>
            </tbody>
          </table>

          <div class="quote-sheet-total">
            <div v-if="isEquipmentQuoteSheet"><span>设备系统合价</span><strong>{{ money(quoteSheetEquipmentSubtotal) }}</strong></div>
            <div v-if="isEquipmentQuoteSheet"><span>费用项合计</span><strong>{{ money(quoteSheetFeeTotal) }}</strong></div>
            <div v-if="!isEquipmentQuoteSheet"><span>商品小计</span><strong>{{ money(quoteSheetSubtotal) }}</strong></div>
            <div v-if="Number(quoteSheetQuote.discountAmount || 0) > 0"><span>优惠金额</span><strong>-{{ money(quoteSheetQuote.discountAmount) }}</strong></div>
            <div class="grand-total"><span>报价合计</span><strong>{{ money(quoteSheetQuote.totalAmount) }}</strong></div>
          </div>

          <div v-if="quoteSheetQuote.remark" class="quote-sheet-remark">
            <span>备注</span>
            <p>{{ quoteSheetQuote.remark }}</p>
          </div>
        </div>
      </div>
    </el-dialog>

    <el-dialog title="发起报价单用章审批" :visible.sync="sealApprovalOpen" width="560px" append-to-body :close-on-click-modal="false">
      <el-alert
        class="seal-approval-alert"
        type="info"
        :closable="false"
        title="请上传需要用章的报价单文件。审批通过后，该报价单才能继续生成合同。">
      </el-alert>
      <el-form ref="sealApprovalForm" :model="sealApprovalForm" :rules="sealApprovalRules" label-width="96px">
        <el-form-item label="报价编号">
          <span>{{ sealApprovalForm.quoteNo || '-' }}</span>
        </el-form-item>
        <el-form-item label="报价单文件" prop="quoteFileObjs">
          <FileUpload
            v-model="sealApprovalForm.quoteFileObjs"
            :limit="5"
            :readOnly="false"
            accept=".xls,.xlsx,.pdf,.doc,.docx"
            @on-change="handleSealFileChange">
          </FileUpload>
        </el-form-item>
        <el-form-item label="用章说明">
          <el-input
            v-model="sealApprovalForm.remark"
            type="textarea"
            :rows="3"
            maxlength="500"
            show-word-limit
            placeholder="可填写报价用章背景、客户要求或补充说明">
          </el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="sealApprovalOpen = false">取消</el-button>
        <el-button type="primary" :loading="sealApprovalLoading" @click="submitSealApproval">提交审批</el-button>
      </span>
    </el-dialog>

    <el-dialog title="报价单用章审批记录" :visible.sync="approvalRecordOpen" width="760px" append-to-body>
      <approveInfo
        v-if="approvalRecordOpen && approvalWorkOrderId"
        :targetId="approvalWorkOrderId"
        targetType="quote"
        @success="handleApprovalSuccess">
      </approveInfo>
    </el-dialog>

  </div>
</template>

<script>
import {
  calculateQuote,
  getQuoteDetail,
  getQuoteTemplateDetail,
  listQuoteTemplates,
  listQuotes,
  removeQuote,
  saveQuote,
  submitQuoteSealApproval
} from '@/api/crm/quote'
import { listProductItems } from '@/api/crm/productCenter'
import { queryBusinessDetail } from '@/api/crm/business'
import download from '@/plugins/download'
import FileUpload from '@/views/crm/components/fileUpload'
import approveInfo from '@/views/crm/components/approveInfo'

const emptyForm = () => ({
  id: null,
  quoteNo: '',
  quoteName: '',
  customerId: null,
  customerName: '',
  businessId: null,
  businessName: '',
  contactId: null,
  contactName: '',
  contactMobile: '',
  quoteDate: '',
  validUntil: '',
  status: 10,
  sealStatus: 0,
  sealWorkOrderId: null,
  sealFileIds: '',
  sealApplyRemark: '',
  approvalTemplateCode: 'quoteSealProcess',
  quoteFileObjs: [],
  discountAmount: 0,
  totalAmount: 0,
  costAmount: 0,
  grossProfit: 0,
  grossMargin: 0,
  riskFlag: 0,
  riskSummary: '',
  contractId: null,
  remark: '',
  itemList: []
})

const EQUIPMENT_TEMPLATE_CODE = 'EQUIPMENT_QUOTE_TEMPLATE:150T_WASTEWATER_SYSTEM'

const EQUIPMENT_TEMPLATE_ITEMS = [
  { module: '改建废水收集池200m3', lineNo: 1, name: '液位控制（数显式液位计）', spec: '', qty: 1, unit: '套', price: 960, service: '包工包料', remark: '北京格瑞威特/瑞莱斯特/美控' },
  { module: '改建废水收集池200m3', lineNo: 2, name: '电磁流量计 DN65,一体式带显示量程：0-20m³/h', spec: '', qty: 1, unit: '台', price: 3000, service: '包工包料', remark: '瑞莱斯特/环扬' },
  { module: '改建废水收集池200m3', lineNo: 3, name: 'PH仪 1-14', spec: '', qty: 1, unit: '台', price: 1680, service: '包工包料', remark: '美控/瑞莱斯特' },
  { module: '改建废水收集池200m3', lineNo: 4, name: '潜污提升泵 流量：Q=10m3/h 扬程：15m 材质：SS304 带SS304耦合', spec: '', qty: 2, unit: '台', price: 5400, service: '包工包料', remark: '新界/南方/江南' },
  { module: '改建废水收集池200m3', lineNo: 5, name: '曝气器，尺寸：Ø120*400mm，通气量10m3/h，安装方式：可提升式', spec: '', qty: 1, unit: '套', price: 1800, service: '包工包料', remark: '国产' },
  { module: '化学预处理', lineNo: 6, name: '气浮机', spec: '处理能力10m³/h,4500*2000*2200mm,材质SS304,含刮渣/搅拌/溶气系统、爬梯平台及扶栏', qty: 1, unit: '台', price: 141600, service: '包工包料', remark: '工源/源申' },
  { module: '化学预处理', lineNo: 7, name: 'PAC加药系统', spec: 'PE桶，1000L,含搅拌机加药泵，配转子流量计', qty: 1, unit: '套', price: 5640, service: '包工包料', remark: '南京瑞洁特' },
  { module: '化学预处理', lineNo: 8, name: 'PAM加药系统', spec: 'PE桶，1000L,含搅拌机加药泵，配转子流量计', qty: 1, unit: '套', price: 5640, service: '包工包料', remark: '' },
  { module: '化学预处理', lineNo: 9, name: '片碱加药系统', spec: 'PE桶，1000L,含搅拌机加药泵，配转子流量计', qty: 1, unit: '套', price: 8040, service: '包工包料', remark: '' },
  { module: '化学预处理', lineNo: 10, name: 'PH仪 1-14', spec: '', qty: 1, unit: '台', price: 1680, service: '包工包料', remark: '美控/瑞莱斯特' },
  { module: '生化A/O系统', lineNo: 11, name: '脉冲布水系统', spec: '含脉冲布水器、布水管、阀件等', qty: 1, unit: '台', price: 9600, service: '包工包料', remark: '南京瑞洁特' },
  { module: '生化A/O系统', lineNo: 12, name: 'pH在线监测仪', spec: '带温度显示', qty: 1, unit: '台', price: 2160, service: '包工包料', remark: '美控/瑞莱斯特' },
  { module: '生化A/O系统', lineNo: 13, name: '水解排泥泵', spec: '潜污泵，Q=5m3/h，扬程15m，SS304，带SS304耦合', qty: 1, unit: '台', price: 4440, service: '包工包料', remark: '新界/南方/江南' },
  { module: '生化A/O系统', lineNo: 14, name: '微孔曝气管', spec: 'Ø63*750mm，通气量5~10m3/h', qty: 1, unit: '套', price: 50400, service: '包工包料', remark: '国产' },
  { module: '生化A/O系统', lineNo: 15, name: '空悬风机', spec: 'Q=14m3/min，P=110kPa', qty: 1, unit: '台', price: 91200, service: '包工包料', remark: '金士顿/苏州精效/章鼓' },
  { module: '生化A/O系统', lineNo: 16, name: '液位控制（数显式液位计）', spec: '', qty: 1, unit: '台', price: 1200, service: '包工包料', remark: '北京格瑞威特/瑞莱斯特/美控' },
  { module: 'MBR系统', lineNo: 17, name: '罗茨风机', spec: 'Q=10m3/min，P=40kPa', qty: 1, unit: '台', price: 32400, service: '包工包料', remark: '宜友/章鼓' },
  { module: 'MBR系统', lineNo: 18, name: 'MBR膜组件', spec: '150m³/d，D-RGE-100-120，4组，单组有效膜面积≥240m2，PVDF膜片，304不锈钢膜架', qty: 1, unit: '套', price: 302400, service: '包工包料', remark: '南京瑞洁特' },
  { module: 'MBR系统', lineNo: 19, name: '膜产水自吸泵', spec: 'Q=10m3/h，扬程15m，吸程4.5m，SS304', qty: 2, unit: '台', price: 2520, service: '包工包料', remark: '新界/南方/江南' },
  { module: 'MBR系统', lineNo: 20, name: 'MBR清洗系统', spec: 'PE桶，1000L,含搅拌机加药泵，配转子流量计', qty: 1, unit: '套', price: 5040, service: '包工包料', remark: '南京瑞洁特' },
  { module: 'MBR系统', lineNo: 21, name: '污泥回流泵', spec: 'Q=15m3/h，扬程15m，吸程4.5m，SS304', qty: 2, unit: '台', price: 3120, service: '包工包料', remark: '新界/南方/江南' },
  { module: 'MBR系统', lineNo: 22, name: '膜池提升泵', spec: 'Q=20m3/h，扬程15m，吸程4.5m，SS304', qty: 2, unit: '台', price: 3240, service: '包工包料', remark: '新界/南方/江南' },
  { module: 'MBR系统', lineNo: 23, name: '饮水罐', spec: '20L，材质PP', qty: 1, unit: '台', price: 1320, service: '包工包料', remark: '国产' },
  { module: 'MBR系统', lineNo: 24, name: '生化池液位控制（数显式液位计）', spec: '', qty: 1, unit: '套', price: 960, service: '包工包料', remark: '北京格瑞威特/瑞莱斯特/美控' },
  { module: 'MBR系统', lineNo: 25, name: '可数据传输真空表', spec: '负压表：-0.1mpa - 0mpa', qty: 1, unit: '台', price: 240, service: '包工包料', remark: '万达' },
  { module: 'MBR系统', lineNo: 26, name: '产水流量计', spec: '电磁流量计 DN65,一体式带显示量程：0-20m³/h', qty: 1, unit: '台', price: 3000, service: '包工包料', remark: '瑞莱斯特/环扬' },
  { module: 'MBR系统', lineNo: 27, name: '风管流量计', spec: '法兰接口，介质气体，DN100', qty: 1, unit: '台', price: 2400, service: '包工包料', remark: '银环/成丰/瑞莱斯特' },
  { module: 'MBR系统', lineNo: 28, name: '膜池液位控制（数显式液位计）', spec: '', qty: 1, unit: '套', price: 960, service: '包工包料', remark: '北京格瑞威特/瑞莱斯特/美控' },
  { module: '清水排放', lineNo: 29, name: '液位控制（数显式液位计）', spec: '', qty: 1, unit: '套', price: 960, service: '包工包料', remark: '北京格瑞威特/瑞莱斯特/美控' },
  { module: '清水排放', lineNo: 30, name: '电磁流量计 DN55,一体式带显示量程：0-20m³/h', spec: '', qty: 1, unit: '台', price: 3000, service: '包工包料', remark: '瑞莱斯特/环扬' },
  { module: '清水排放', lineNo: 31, name: '清水排放', spec: '现场制作', qty: 1, unit: '套', price: 0, service: '包工包料', remark: '乙方根据现场制作' },
  { module: '污泥脱水系统', lineNo: 32, name: '液位控制', spec: '', qty: 1, unit: '套', price: 960, service: '包工包料', remark: '美控/瑞莱斯特' },
  { module: '污泥脱水系统', lineNo: 33, name: '污泥提升泵', spec: '潜污泵，Q=5m3/h，扬程15m，SS304，带SS304耦合', qty: 2, unit: '台', price: 4440, service: '包工包料', remark: '新界/南方/江南' },
  { module: '污泥脱水系统', lineNo: 34, name: '叠螺脱水机', spec: 'RDL251，15-30ds·kg/h，处理污泥量3-5吨/小时，不锈钢，配套控制柜', qty: 1, unit: '套', price: 45600, service: '包工包料', remark: '瑞德/弗斯特' },
  { module: '污泥脱水系统', lineNo: 35, name: 'PAM加药装置', spec: 'JYZZ-1000-100*1,配套搅拌机和计量泵', qty: 1, unit: '套', price: 5640, service: '包工包料', remark: '南京瑞洁特' },
  { module: '污泥脱水系统', lineNo: 36, name: '污泥池搅拌机', spec: '潜水搅拌器，N=1.1kw，SS304，含提升装置', qty: 1, unit: '台', price: 6600, service: '包工包料', remark: '兰环/奥博尔/蓝污' },
  { module: '污水配套', lineNo: 37, name: '管道、阀门及工作台等', spec: '配套 管道阀门材质：UPVC+碳钢管', qty: 1, unit: '批', price: 82000, service: '包工包料', remark: '需要根据现场进一步设计，此项包含实际偏差' },
  { module: '污水配套', lineNo: 38, name: '电气控制', spec: '污水处理系统PLC电控', qty: 1, unit: '套', price: 80000, service: '包工包料', remark: '' },
  { module: '污水配套', lineNo: 39, name: '污水站内电线、电缆', spec: '', qty: 1, unit: '批', price: 78000, service: '包工包料', remark: '' },
  { module: '污水配套', lineNo: 40, name: '污水站内桥架及线管', spec: '', qty: 1, unit: '批', price: 0, service: '包工包料', remark: '' }
]

const EQUIPMENT_TEMPLATE_FEES = [
  { name: '设备安装', rule: '设备系统合价 * 20%', rate: 0.2, unit: '项', service: '费用项', remark: '' },
  { name: '成套设备调试/联动运行/污泥培养和驯化/人员培训', rule: '固定金额', amount: 299553, unit: '项', service: '费用项', remark: '系统自动化控制调试、多模块联动，含污泥接种费' },
  { name: '安全措施', rule: '设备系统合价 * 5%', rate: 0.05, unit: '项', service: '费用项', remark: '' },
  { name: '税费', rule: '设备系统合价及费用项综合测算，示例口径约9%', amount: 146061, unit: '项', service: '费用项', remark: '' },
  { name: '设计费', rule: '设备系统合价 * 5%', rate: 0.05, unit: '项', service: '费用项', remark: '前期技术服务、方案验证、设计、基建指导' }
]

export default {
  name: 'QuoteCalculation',
  components: {
    FileUpload,
    approveInfo
  },
  props: {
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      showSearch: true,
      formOpen: false,
      submitLoading: false,
      calculateLoading: false,
      templateLoading: false,
      selectedTemplateCode: '',
      productSelectorOpen: false,
      productLoading: false,
      quoteSheetOpen: false,
      quoteSheetLoading: false,
      sealApprovalOpen: false,
      sealApprovalLoading: false,
      approvalRecordOpen: false,
      approvalWorkOrderId: null,
      routeBusinessIdHandled: null,
      routeQuoteIdHandled: null,
      dataList: [],
      templateList: [],
      productList: [],
      queryParams: {
        keyword: '',
        status: null,
        riskFlag: null
      },
      productQuery: {
        keyword: '',
        categoryName: '',
        status: null
      },
      form: emptyForm(),
      quoteSheet: {
        quote: emptyForm(),
        itemList: []
      },
      sealApprovalForm: {
        quoteId: null,
        quoteNo: '',
        quoteFileObjs: [],
        remark: ''
      },
      statusOptions: [
        { value: 10, label: '草稿' },
        { value: 20, label: '待用章' },
        { value: 30, label: '用章审批中' },
        { value: 40, label: '用章通过' },
        { value: 50, label: '已转合同' },
        { value: 90, label: '已作废' }
      ],
      rules: {
        quoteName: [{ required: true, message: '报价名称不能为空', trigger: 'blur' }],
        customerName: [{ required: true, message: '客户名称不能为空', trigger: 'blur' }]
      },
      sealApprovalRules: {
        quoteFileObjs: [
          { required: true, type: 'array', min: 1, message: '请上传报价单文件', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    formTitle() {
      return this.form.id ? '编辑报价测算' : '新建报价测算'
    },
    summary() {
      const total = this.dataList.length
      const totalAmount = this.dataList.reduce((sum, item) => sum + Number(item.totalAmount || 0), 0)
      const costAmount = this.dataList.reduce((sum, item) => sum + Number(item.costAmount || 0), 0)
      const marginTotal = this.dataList.reduce((sum, item) => sum + Number(item.grossMargin || 0), 0)
      const riskCount = this.dataList.filter(item => Number(item.riskFlag) === 1).length
      return {
        total,
        totalAmount,
        costAmount,
        avgMargin: total ? (marginTotal / total).toFixed(2) : '0.00',
        riskCount
      }
    },
    quoteSheetQuote() {
      return this.quoteSheet.quote || emptyForm()
    },
    quoteSheetItems() {
      return this.quoteSheet.itemList || []
    },
    quoteSheetSubtotal() {
      return this.quoteSheetItems.reduce((sum, item) => sum + Number(item.amount || 0), 0)
    },
    isEquipmentQuoteSheet() {
      return this.quoteSheetItems.some(item => this.isEquipmentTemplateItem(item))
    },
    quoteSheetEquipmentGroups() {
      return this.buildEquipmentGroups(this.quoteSheetItems)
    },
    quoteSheetFeeItems() {
      return this.quoteSheetItems.filter(item => this.isEquipmentFeeItem(item))
    },
    quoteSheetEquipmentSubtotal() {
      return this.quoteSheetItems
        .filter(item => this.isEquipmentDetailItem(item))
        .reduce((sum, item) => sum + Number(item.amount || 0), 0)
    },
    quoteSheetFeeTotal() {
      return this.quoteSheetFeeItems.reduce((sum, item) => sum + Number(item.amount || 0), 0)
    }
  },
  created() {
    if (!this.embedded) {
      this.getList()
      this.openFromRouteBusiness()
      this.openFromRouteQuote()
    }
  },
  watch: {
    '$route.query.businessId'(val) {
      if (!this.embedded && val) {
        this.openFromRouteBusiness()
      }
    },
    '$route.query.quoteId'(val) {
      if (!val) {
        this.routeQuoteIdHandled = null
      } else if (!this.embedded) {
        this.openFromRouteQuote()
      }
    }
  },
  methods: {
    getList() {
      this.loading = true
      listQuotes(this.cleanParams(this.queryParams))
        .then(response => {
          this.dataList = (response && response.data) || []
        })
        .catch(() => {
          this.dataList = []
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleQuery() {
      this.getList()
    },
    resetQuery() {
      this.resetForm('queryForm')
      this.queryParams = { keyword: '', status: null, riskFlag: null }
      this.getList()
    },
    newQuoteForm() {
      const today = new Date()
      const currentUserContact = this.currentUserContact()
      return {
        ...emptyForm(),
        contactName: currentUserContact.contactName,
        contactMobile: currentUserContact.contactMobile,
        quoteDate: this.formatDate(today),
        validUntil: this.formatDate(this.addDays(today, 30))
      }
    },
    handleAdd() {
      this.form = this.newQuoteForm()
      this.selectedTemplateCode = ''
      this.formOpen = true
      this.ensureTemplateList()
      this.$nextTick(() => this.clearValidate())
    },
    openFromBusiness(businessId) {
      this.handleAddFromBusiness(businessId)
    },
    openFromRouteBusiness() {
      const businessId = this.$route && this.$route.query ? this.$route.query.businessId : null
      if (!businessId || String(this.routeBusinessIdHandled) === String(businessId)) {
        return
      }
      this.routeBusinessIdHandled = businessId
      this.handleAddFromBusiness(businessId)
    },
    openFromRouteQuote() {
      const quoteId = this.$route && this.$route.query ? this.$route.query.quoteId : null
      if (!quoteId || String(this.routeQuoteIdHandled) === String(quoteId)) return
      this.routeQuoteIdHandled = quoteId
      this.handleView({ id: quoteId })
    },
    handleAddFromBusiness(businessId) {
      const loading = this.$loading({
        lock: true,
        text: '商机信息带入中',
        spinner: 'el-icon-loading'
      })
      queryBusinessDetail({ id: businessId }).then(businessResponse => {
        const business = (businessResponse && businessResponse.data) || {}
        if (!business.id) {
          this.$message.warning('未查询到商机信息，请手动新建报价')
          this.handleAdd()
          return
        }
        const currentUserContact = this.currentUserContact()
        this.form = {
          ...this.newQuoteForm(),
          quoteName: this.buildQuoteName(business),
          customerId: business.customerId || null,
          customerName: business.customerName || '',
          businessId: business.id,
          businessName: business.businessName || '',
          contactId: null,
          contactName: currentUserContact.contactName,
          contactMobile: currentUserContact.contactMobile,
          remark: business.requirementsDesc ? `采购需求：${business.requirementsDesc}` : ''
        }
        this.selectedTemplateCode = ''
        this.formOpen = true
        this.ensureTemplateList()
        this.$nextTick(() => this.clearValidate())
        this.$message.success('已带入商机基础信息，请选择商品明细')
      }).catch(() => {
        this.$message.error('商机信息带入失败，请手动新建报价')
        this.handleAdd()
      }).finally(() => {
        loading.close()
        this.clearRouteBusinessId()
      })
    },
    clearRouteBusinessId() {
      if (!this.$route || !this.$route.query || !this.$route.query.businessId) {
        return
      }
      const query = { ...this.$route.query }
      delete query.businessId
      this.routeBusinessIdHandled = null
      this.$router.replace({ path: this.$route.path, query })
    },
    buildQuoteName(business) {
      const customerName = business.customerName || ''
      const businessName = business.businessName || ''
      return [customerName, businessName].filter(Boolean).join('-') + '报价'
    },
    currentUserContact() {
      const user = (this.$store && this.$store.state && this.$store.state.user) || {}
      return {
        contactName: user.nickName || user.name || '',
        contactMobile: user.phonenumber || user.phoneNumber || user.mobile || ''
      }
    },
    handleView(row) {
      this.handleEdit(row)
    },
    handleEdit(row) {
      getQuoteDetail(row.id).then(response => {
        this.form = this.toForm((response && response.data) || {})
        this.selectedTemplateCode = ''
        this.formOpen = true
        this.ensureTemplateList()
        this.$nextTick(() => this.clearValidate())
      })
    },
    handleConfirm(row) {
      this.openSealApproval(row)
    },
    handleCreateContract(row) {
      if (row.contractId) {
        this.$router.push({ path: `/crm/contract/detail/${row.contractId}` })
        return
      }
      if (Number(row.status) === 90) {
        this.$message.warning('已作废报价不能生成合同')
        return
      }
      if (!this.canCreateContract(row)) {
        this.$message.warning('报价单用章审批通过后才能生成合同')
        return
      }
      this.$router.push({
        path: '/crm/contract/manage',
        query: {
          quoteId: row.id
        }
      })
    },
    openProductSelector() {
      this.productSelectorOpen = true
      this.getProductList()
    },
    getProductList() {
      this.productLoading = true
      listProductItems(this.cleanParams(this.productQuery))
        .then(response => {
          this.productList = ((response && response.data) || []).filter(item => Number(item.status) !== 3)
        })
        .finally(() => {
          this.productLoading = false
        })
    },
    selectProduct(product) {
      this.form.itemList.push({
        productId: product.id,
        productCode: product.productCode,
        productName: product.productName,
        productVersion: product.versionNo || 'V1',
        categoryName: product.categoryName,
        subCategoryName: product.subCategoryName,
        productModel: product.productModel,
        saleUnit: product.saleUnit || product.deliveryUnit || product.purchaseUnit || '项',
        quantity: 1,
        standardPrice: Number(product.standardPrice || 0),
        floorPrice: Number(product.floorPrice || 0),
        salePrice: Number(product.standardPrice || product.floorPrice || 0),
        discountRate: 100,
        amount: Number(product.standardPrice || product.floorPrice || 0),
        standardCost: Number(product.standardCost || 0),
        estimatedCost: Number(product.standardCost || 0),
        taxRate: Number(product.taxRate || 0),
        targetGrossMargin: Number(product.targetGrossMargin || 0),
        deliveryScopeSnapshot: product.deliveryScope,
        nonStandardFlag: Number(product.nonStandardFlag || 0),
        riskFlags: Number(product.status) === 2 ? 'PRICE_PENDING' : '',
        remark: ''
      })
      this.productSelectorOpen = false
      this.recalculateLocal()
      this.calculateRemote(true)
    },
    handleTemplateVisible(visible) {
      if (visible) {
        this.ensureTemplateList()
      }
    },
    ensureTemplateList() {
      if (this.templateList.length || this.templateLoading) {
        return
      }
      this.templateLoading = true
      listQuoteTemplates().then(response => {
        this.templateList = (response && response.data) || []
        if (!this.selectedTemplateCode && this.templateList.length) {
          this.selectedTemplateCode = this.templateList[0].templateCode
        }
      }).finally(() => {
        this.templateLoading = false
      })
    },
    loadSelectedTemplate() {
      if (!this.selectedTemplateCode) {
        this.$message.warning('请先选择报价模板')
        return
      }
      if (!this.form.itemList.length) {
        this.fetchSelectedTemplate('replace')
        return
      }
      this.$confirm('当前已有商品明细，请选择模板载入方式。覆盖会清空当前明细，追加会保留现有明细并把模板明细加到后面。', '载入报价模板', {
        confirmButtonText: '覆盖当前明细',
        cancelButtonText: '追加到当前明细',
        distinguishCancelAndClose: true,
        type: 'warning'
      }).then(() => {
        this.fetchSelectedTemplate('replace')
      }).catch(action => {
        if (action === 'cancel') {
          this.fetchSelectedTemplate('append')
        }
      })
    },
    fetchSelectedTemplate(mode = 'replace') {
      this.templateLoading = true
      getQuoteTemplateDetail(this.selectedTemplateCode).then(response => {
        this.applyQuoteTemplate((response && response.data) || {}, mode)
      }).finally(() => {
        this.templateLoading = false
      })
    },
    applyQuoteTemplate(templateDetail = {}, mode = 'replace') {
      const appendMode = mode === 'append'
      const startIndex = appendMode ? this.form.itemList.length : 0
      const itemList = (templateDetail.itemList || [])
        .filter(item => !this.isTechnicalTemplateItem(item))
        .map((item, index) => this.normalizeTemplateItem(item, index, startIndex))
      if (!itemList.length) {
        this.$message.warning('报价模板暂无明细，请检查模板配置')
        return
      }
      this.form.itemList = appendMode ? this.form.itemList.concat(itemList) : itemList
      const template = templateDetail.template || {}
      if (!this.form.quoteName) {
        this.form.quoteName = [this.form.customerName, template.templateName || '报价单'].filter(Boolean).join('-')
      }
      this.$message.success(`${appendMode ? '已追加' : '已载入'}${template.templateName || '报价模板'}`)
      this.recalculateLocal()
    },
    normalizeTemplateItem(item, index, offset = 0) {
      const quantity = Number(item.quantity || 0)
      const salePrice = Number(item.salePrice || 0)
      const discountRate = Number(item.discountRate == null ? 100 : item.discountRate)
      const lineNo = offset + index + 1
      return {
        ...item,
        productId: item.productId || null,
        productCode: item.productCode || `RJT-TPL-${String(lineNo).padStart(3, '0')}`,
        productName: item.productName || '报价明细',
        productVersion: item.productVersion || 'V1',
        categoryName: item.categoryName || '设备明细',
        subCategoryName: item.subCategoryName || '设备明细',
        productModel: item.productModel || '',
        saleUnit: item.saleUnit || '项',
        quantity,
        standardPrice: Number(item.standardPrice == null ? salePrice : item.standardPrice),
        floorPrice: Number(item.floorPrice || 0),
        salePrice,
        discountRate,
        amount: Number(item.amount == null ? this.round2(quantity * salePrice * discountRate / 100) : item.amount),
        standardCost: Number(item.standardCost || 0),
        estimatedCost: Number(item.estimatedCost || 0),
        taxRate: Number(item.taxRate || 13),
        targetGrossMargin: Number(item.targetGrossMargin || 0),
        pricingRuleSnapshot: item.pricingRuleSnapshot || '',
        bomSnapshot: item.bomSnapshot || (this.selectedTemplateCode ? `QUOTE_TEMPLATE:${this.selectedTemplateCode}` : ''),
        deliveryScopeSnapshot: item.deliveryScopeSnapshot || '',
        nonStandardFlag: Number(item.nonStandardFlag == null ? 1 : item.nonStandardFlag),
        riskFlags: item.riskFlags || 'NON_STANDARD,PRICE_PENDING',
        remark: item.remark || '',
        lineNo
      }
    },
    addNonStandard() {
      this.form.itemList.push({
        productId: null,
        productCode: '',
        productName: '非标项',
        productVersion: 'V1',
        categoryName: '非标商品',
        productModel: '',
        saleUnit: '项',
        quantity: 1,
        standardPrice: 0,
        floorPrice: 0,
        salePrice: 0,
        discountRate: 100,
        amount: 0,
        standardCost: 0,
        estimatedCost: 0,
        taxRate: 13,
        targetGrossMargin: 0,
        nonStandardFlag: 1,
        riskFlags: 'NON_STANDARD,PRICE_PENDING',
        remark: ''
      })
      this.recalculateLocal()
    },
    removeItem(index) {
      this.form.itemList.splice(index, 1)
      this.recalculateLocal()
    },
    calculateRemote(silent = false) {
      if (!this.form.itemList.length) {
        this.$message.warning('请先添加报价明细')
        return
      }
      this.calculateLoading = true
      calculateQuote(this.buildSubmitData())
        .then(response => {
          this.form = this.toForm((response && response.data) || {})
          if (!silent) {
            this.$message.success('测算完成，已更新金额、成本、毛利和风险，不会自动保存')
          }
        })
        .finally(() => {
          this.calculateLoading = false
        })
    },
    recalculateLocal() {
      let totalAmount = 0
      let costAmount = 0
      const riskSet = new Set()
      this.form.itemList.forEach(item => {
        const quantity = Number(item.quantity || 0)
        const salePrice = Number(item.salePrice || 0)
        const discountRate = Number(item.discountRate == null ? 100 : item.discountRate)
        item.amount = this.round2(quantity * salePrice * discountRate / 100)
        if (!item.estimatedCost || Number(item.estimatedCost) === 0) {
          item.estimatedCost = this.round2(quantity * Number(item.standardCost || 0))
        }
        item.grossProfit = this.round2(Number(item.amount || 0) - Number(item.estimatedCost || 0))
        item.grossMargin = Number(item.amount || 0) ? this.round2(item.grossProfit * 100 / Number(item.amount || 0)) : 0
        item.riskFlags = this.localRiskFlags(item)
        this.riskTags(item.riskFlags).forEach(tag => riskSet.add(tag))
        totalAmount += Number(item.amount || 0)
        costAmount += Number(item.estimatedCost || 0)
      })
      totalAmount = Math.max(0, this.round2(totalAmount - Number(this.form.discountAmount || 0)))
      this.form.totalAmount = totalAmount
      this.form.costAmount = this.round2(costAmount)
      this.form.grossProfit = this.round2(totalAmount - costAmount)
      this.form.grossMargin = totalAmount ? this.round2(this.form.grossProfit * 100 / totalAmount) : 0
      this.form.riskFlag = riskSet.size ? 1 : 0
      this.form.riskSummary = [...riskSet].join(',')
    },
    localRiskFlags(item) {
      const risks = new Set(this.riskTags(item.riskFlags))
      if (Number(item.nonStandardFlag) === 1 || !item.productId) risks.add('NON_STANDARD')
      if (!Number(item.standardPrice || 0) || !Number(item.standardCost || 0)) risks.add('PRICE_PENDING')
      if (Number(item.floorPrice || 0) > 0 && Number(item.salePrice || 0) < Number(item.floorPrice || 0)) risks.add('LOW_PRICE')
      if (Number(item.targetGrossMargin || 0) > 0 && Number(item.grossMargin || 0) < Number(item.targetGrossMargin || 0)) risks.add('LOW_MARGIN')
      return [...risks].join(',')
    },
    submitForm(generateQuoteSheet = true) {
      this.$refs.quoteForm.validate(valid => {
        if (!valid) return
        if (!this.form.itemList.length) {
          this.$message.warning('请添加报价明细')
          return
        }
        this.recalculateLocal()
        this.submitLoading = true
        const submitData = this.buildSubmitData()
        submitData.status = generateQuoteSheet ? 20 : 10
        saveQuote(submitData)
          .then(response => {
            const quoteId = response && response.data ? response.data : this.form.id
            this.$message.success(generateQuoteSheet ? '保存成功，已生成Excel报价单' : '保存成功')
            this.formOpen = false
            if (!this.embedded) {
              this.getList()
            }
            this.$emit('saved', { quoteId, quote: this.form })
            if (generateQuoteSheet && quoteId) {
              this.openQuoteSheet(quoteId, { autoExportExcel: true })
            }
          })
          .finally(() => {
            this.submitLoading = false
          })
      })
    },
    buildSubmitData() {
      return {
        ...this.form,
        quoteDate: this.form.quoteDate || this.formatDate(new Date()),
        validUntil: this.form.validUntil || null,
        itemList: this.form.itemList.map((item, index) => ({
          ...item,
          lineNo: index + 1,
          quantity: Number(item.quantity || 0),
          salePrice: Number(item.salePrice || 0),
          discountRate: Number(item.discountRate == null ? 100 : item.discountRate),
          estimatedCost: Number(item.estimatedCost || 0)
        }))
      }
    },
    handleQuoteSheet(row) {
      this.openQuoteSheet(row && row.id)
    },
    openQuoteSheet(id, options = {}) {
      if (!id) {
        this.$message.warning('请先保存报价')
        return
      }
      this.quoteSheetOpen = true
      this.quoteSheetLoading = true
      getQuoteDetail(id)
        .then(response => {
          const form = this.toForm((response && response.data) || {})
          this.quoteSheet = {
            quote: form,
            itemList: form.itemList || []
          }
          if (options.autoExportExcel) {
            this.$nextTick(() => this.exportQuoteExcel())
          }
        })
        .finally(() => {
          this.quoteSheetLoading = false
        })
    },
    printQuoteSheet() {
      if (!this.$refs.quoteSheetPrint) {
        return
      }
      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        this.$message.warning('浏览器拦截了打印窗口，请允许弹窗后重试')
        return
      }
      printWindow.document.write(`
        <html>
          <head>
            <title>${this.quoteSheetFileName()}</title>
            <style>${this.printStyle()}</style>
          </head>
          <body>${this.$refs.quoteSheetPrint.outerHTML}</body>
        </html>
      `)
      printWindow.document.close()
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
        printWindow.close()
      }, 300)
    },
    exportQuoteExcel() {
      if (!this.quoteSheetItems.length) {
        this.$message.warning('暂无报价明细，无法生成Excel报价单')
        return
      }
      const quote = this.quoteSheetQuote
      if (!quote.id) {
        this.$message.warning('请先保存报价后再导出Excel报价单')
        return
      }
      download.post('/crmQuote/export', { id: quote.id })
    },
    exportContractDraft(row) {
      if (!row || !row.id) {
        this.$message.warning('请先保存报价单')
        return
      }
      if (Number(row.status) === 90) {
        this.$message.warning('已作废报价不能生成合同草稿')
        return
      }
      download.post('/crmQuote/exportContractDraft', { id: row.id })
    },
    openSealApproval(row) {
      if (!row || !row.id) {
        this.$message.warning('请先保存报价单')
        return
      }
      if (!this.canSubmitSealApproval(row)) {
        this.$message.warning('当前报价单不能发起用章审批')
        return
      }
      getQuoteDetail(row.id).then(response => {
        const detail = (response && response.data) || {}
        const form = this.toForm(detail)
        this.sealApprovalForm = {
          quoteId: form.id,
          quoteNo: form.quoteNo,
          quoteFileObjs: form.quoteFileObjs || [],
          remark: form.sealApplyRemark || ''
        }
        this.sealApprovalOpen = true
        this.$nextTick(() => {
          if (this.$refs.sealApprovalForm) {
            this.$refs.sealApprovalForm.clearValidate()
          }
        })
      })
    },
    handleSealFileChange(fileObjs) {
      this.sealApprovalForm.quoteFileObjs = fileObjs || []
      this.$nextTick(() => {
        if (this.$refs.sealApprovalForm) {
          this.$refs.sealApprovalForm.validateField('quoteFileObjs')
        }
      })
    },
    submitSealApproval() {
      this.$refs.sealApprovalForm.validate(valid => {
        if (!valid) return
        if (!this.sealApprovalForm.quoteFileObjs.length) {
          this.$message.warning('请上传报价单文件')
          return
        }
        this.sealApprovalLoading = true
        submitQuoteSealApproval({
          quoteId: this.sealApprovalForm.quoteId,
          approvalTemplateCode: 'quoteSealProcess',
          quoteFileObjs: this.sealApprovalForm.quoteFileObjs,
          remark: this.sealApprovalForm.remark
        }).then(response => {
          this.$message.success('报价单用章审批已提交')
          this.sealApprovalOpen = false
          const workOrderId = response && response.data
          if (workOrderId) {
            this.approvalWorkOrderId = workOrderId
            this.approvalRecordOpen = true
          }
          if (!this.embedded) {
            this.getList()
          }
          this.$emit('saved', { quoteId: this.sealApprovalForm.quoteId, action: 'sealApproval' })
        }).finally(() => {
          this.sealApprovalLoading = false
        })
      })
    },
    openApprovalRecord(row) {
      if (!row || !row.sealWorkOrderId) {
        this.$message.warning('暂无用章审批记录')
        return
      }
      this.approvalWorkOrderId = row.sealWorkOrderId
      this.approvalRecordOpen = true
    },
    handleApprovalSuccess() {
      if (!this.embedded) {
        this.getList()
      }
      this.$emit('saved', { quoteId: null, action: 'approvalRecord' })
    },
    canEditQuote(row) {
      const status = Number(row && row.status)
      const sealStatus = Number((row && row.sealStatus) || 0)
      return ![1, 2].includes(sealStatus) && ![50, 90].includes(status)
    },
    canSubmitSealApproval(row) {
      const status = Number(row && row.status)
      const sealStatus = Number((row && row.sealStatus) || 0)
      return !!(row && row.id) && !row.contractId && ![1, 2].includes(sealStatus) && ![50, 90].includes(status)
    },
    canCreateContract(row) {
      return Number(row && row.status) === 40 || Number(row && row.sealStatus) === 2
    },
    toForm(data) {
      const quote = data.quote || {}
      return {
        ...emptyForm(),
        ...quote,
        quoteFileObjs: data.quoteFileObjs || [],
        discountAmount: Number(quote.discountAmount || 0),
        totalAmount: Number(quote.totalAmount || 0),
        costAmount: Number(quote.costAmount || 0),
        grossProfit: Number(quote.grossProfit || 0),
        grossMargin: Number(quote.grossMargin || 0),
        itemList: (data.itemList || []).map(item => ({
          ...item,
          quantity: Number(item.quantity || 0),
          standardPrice: Number(item.standardPrice || 0),
          floorPrice: Number(item.floorPrice || 0),
          salePrice: Number(item.salePrice || 0),
          discountRate: Number(item.discountRate == null ? 100 : item.discountRate),
          amount: Number(item.amount || 0),
          standardCost: Number(item.standardCost || 0),
          estimatedCost: Number(item.estimatedCost || 0),
          grossProfit: Number(item.grossProfit || 0),
          grossMargin: Number(item.grossMargin || 0),
          targetGrossMargin: Number(item.targetGrossMargin || 0),
          nonStandardFlag: Number(item.nonStandardFlag || 0)
        }))
      }
    },
    clearValidate() {
      if (this.$refs.quoteForm) {
        this.$refs.quoteForm.clearValidate()
      }
    },
    riskTags(value) {
      return String(value || '').split(',').filter(Boolean)
    },
    riskText(value) {
      const text = this.riskTags(value).map(this.riskLabel).join('、')
      return text || '无风险'
    },
    riskLabel(value) {
      const map = {
        LOW_PRICE: '低价',
        LOW_MARGIN: '低毛利',
        PRICE_PENDING: '待完善',
        NON_STANDARD: '非标'
      }
      return map[value] || value
    },
    isTechnicalTemplateItem(item) {
      if (!item) {
        return false
      }
      const productCode = String(item.productCode || '')
      if (productCode.indexOf('RJT-PARAM-') === 0) {
        return true
      }
      const fields = [
        item.categoryName,
        item.subCategoryName,
        item.productName,
        item.productModel,
        item.pricingRuleSnapshot,
        item.deliveryScopeSnapshot,
        item.remark
      ]
      return fields.some(value => this.isTechnicalParamText(value))
    },
    isTechnicalParamText(value) {
      const text = String(value || '')
      return text.indexOf('技术参数') !== -1 ||
        text.indexOf('参数展示') !== -1 ||
        text.indexOf('组件参数') !== -1 ||
        text.indexOf('展示参数') !== -1
    },
    quoteTypeLabel(value) {
      const map = {
        EQUIPMENT: '设备型',
        MEMBRANE: '膜组件',
        REPLACEMENT: '换膜',
        SERVICE: '服务型'
      }
      return map[value] || value || '模板'
    },
    riskTagType(value) {
      if (value === 'LOW_PRICE' || value === 'LOW_MARGIN') return 'danger'
      if (value === 'PRICE_PENDING') return 'warning'
      return 'info'
    },
    hasRisk(row, risk) {
      return this.riskTags(row.riskFlags).includes(risk)
    },
    isEquipmentTemplateItem(item) {
      return String((item && item.bomSnapshot) || '').indexOf(EQUIPMENT_TEMPLATE_CODE) !== -1
    },
    isEquipmentDetailItem(item) {
      return this.isEquipmentTemplateItem(item) && String(item.subCategoryName || '') !== '费用项'
    },
    isEquipmentFeeItem(item) {
      return this.isEquipmentTemplateItem(item) && String(item.subCategoryName || '') === '费用项'
    },
    buildEquipmentGroups(items) {
      const groups = []
      const groupMap = {}
      ;(items || []).filter(item => this.isEquipmentDetailItem(item)).forEach(item => {
        const name = item.categoryName || '未分组'
        if (!groupMap[name]) {
          groupMap[name] = { name, items: [], subtotal: 0 }
          groups.push(groupMap[name])
        }
        groupMap[name].items.push(item)
        groupMap[name].subtotal = this.round2(Number(groupMap[name].subtotal || 0) + Number(item.amount || 0))
      })
      return groups
    },
    equipmentGroupLabel(index) {
      const labels = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十']
      return labels[index] || String(index + 1)
    },
    feeDisplayRule(item) {
      return item.pricingRuleSnapshot || item.productModel || '-'
    },
    statusText(value) {
      const item = this.statusOptions.find(option => Number(option.value) === Number(value))
      return item ? item.label : '草稿'
    },
    statusTag(value) {
      const map = { 10: 'info', 20: 'warning', 30: 'warning', 40: 'success', 50: '', 90: 'info' }
      return map[Number(value)] || 'info'
    },
    sealStatusText(value) {
      const map = { 0: '未发起', 1: '审批中', 2: '已通过', 3: '已驳回', 4: '已取消' }
      return map[Number(value || 0)] || '未发起'
    },
    sealStatusTag(value) {
      const map = { 0: 'info', 1: 'warning', 2: 'success', 3: 'danger', 4: 'info' }
      return map[Number(value || 0)] || 'info'
    },
    productStatusText(value) {
      const map = { 1: '启用', 2: '待完善', 3: '停用' }
      return map[Number(value)] || '启用'
    },
    productStatusTag(value) {
      const map = { 1: 'success', 2: 'warning', 3: 'info' }
      return map[Number(value)] || 'success'
    },
    money(value) {
      if (value === null || value === undefined || value === '') return '-'
      return `¥${Number(value || 0).toFixed(2)}`
    },
    numberText(value) {
      if (value === null || value === undefined || value === '') return '0.00'
      return Number(value || 0).toFixed(2)
    },
    quoteDisplayDate(value) {
      return value || this.formatDate(new Date())
    },
    formatDate(date) {
      const current = date instanceof Date ? date : new Date(date)
      const year = current.getFullYear()
      const month = String(current.getMonth() + 1).padStart(2, '0')
      const day = String(current.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    addDays(date, days) {
      const current = date instanceof Date ? new Date(date.getTime()) : new Date(date)
      current.setDate(current.getDate() + Number(days || 0))
      return current
    },
    quoteSheetFileName() {
      const quote = this.quoteSheetQuote
      return this.safeFileName(`报价单_${quote.quoteNo || quote.quoteName || this.formatDate(new Date())}`)
    },
    copyText(text, label = '内容') {
      if (!text) {
        return
      }
      const onSuccess = () => this.$message.success(`${label}已复制`)
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(String(text)).then(onSuccess).catch(() => this.copyTextFallback(text, onSuccess))
        return
      }
      this.copyTextFallback(text, onSuccess)
    },
    copyTextFallback(text, onSuccess) {
      const input = document.createElement('textarea')
      input.value = String(text)
      input.setAttribute('readonly', 'readonly')
      input.style.position = 'fixed'
      input.style.top = '-9999px'
      document.body.appendChild(input)
      input.select()
      try {
        document.execCommand('copy')
        onSuccess && onSuccess()
      } finally {
        document.body.removeChild(input)
      }
    },
    safeFileName(value) {
      return String(value || '报价单').replace(/[\\/:*?"<>|]/g, '_')
    },
    printStyle() {
      return `
        body{margin:0;background:#fff;color:#1f2933;font-family:Arial,"Microsoft YaHei",sans-serif;}
        .quote-sheet-document{width:980px;margin:0 auto;padding:28px 32px;background:#fff;}
        .quote-sheet-header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #1f2933;padding-bottom:18px;margin-bottom:18px;}
        .quote-sheet-header h1{margin:0 0 8px;font-size:28px;letter-spacing:0;font-weight:700;}
        .quote-sheet-header p{margin:0;color:#5f6b7a;font-size:14px;}
        .quote-sheet-no{text-align:right;}
        .quote-sheet-no span{display:block;color:#6b7280;font-size:13px;margin-bottom:6px;}
        .quote-sheet-no strong{font-size:18px;}
        .quote-sheet-meta{display:grid;grid-template-columns:repeat(3,1fr);border:1px solid #d8dee8;border-bottom:0;margin-bottom:18px;}
        .quote-sheet-meta div{padding:10px 12px;border-right:1px solid #d8dee8;border-bottom:1px solid #d8dee8;min-height:44px;}
        .quote-sheet-meta div:nth-child(3n){border-right:0;}
        .quote-sheet-meta span{display:block;color:#6b7280;font-size:12px;margin-bottom:4px;}
        .quote-sheet-meta strong{font-size:14px;font-weight:600;}
        .quote-sheet-table{width:100%;border-collapse:collapse;font-size:12px;}
        .quote-sheet-table th{background:#f0f3f8;color:#1f2933;border:1px solid #cfd7e3;padding:9px 8px;text-align:left;}
        .quote-sheet-table td{border:1px solid #d8dee8;padding:8px;vertical-align:top;line-height:1.45;}
        .quote-sheet-table small{display:block;color:#6b7280;margin-top:4px;}
        .quote-sheet-table .center{text-align:center;}
        .quote-sheet-table .number{text-align:right;white-space:nowrap;}
        .equipment-group-row td{background:#eef3fb;font-weight:700;}
        .equipment-subtotal-row td{background:#f8fafc;font-weight:700;}
        .quote-sheet-total{width:360px;margin:18px 0 0 auto;border:1px solid #d8dee8;}
        .quote-sheet-total div{display:flex;justify-content:space-between;padding:10px 12px;border-bottom:1px solid #d8dee8;}
        .quote-sheet-total div:last-child{border-bottom:0;}
        .quote-sheet-total .grand-total{background:#f8fafc;font-size:16px;}
        .quote-sheet-remark{margin-top:18px;border:1px solid #d8dee8;padding:12px;}
        .quote-sheet-remark span{display:block;color:#6b7280;font-size:12px;margin-bottom:6px;}
        .quote-sheet-remark p{margin:0;white-space:pre-wrap;line-height:1.6;}
        @page{size:A4 landscape;margin:10mm;}
      `
    },
    round2(value) {
      return Math.round(Number(value || 0) * 100) / 100
    },
    cleanParams(params) {
      const result = {}
      Object.keys(params || {}).forEach(key => {
        if (params[key] !== '' && params[key] !== null && params[key] !== undefined) {
          result[key] = params[key]
        }
      })
      return result
    }
  }
}
</script>

<style scoped>
.quote-page {
  background: #f5f7fa;
}

.quote-page--embedded {
  padding: 0;
  background: transparent;
}

.quote-query,
.product-selector-query {
  padding: 12px 12px 0;
  background: #fff;
  border: 1px solid #e6ebf2;
  border-radius: 4px;
}

.total-count {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin: 8px 0 12px;
  color: #606266;
  font-size: 13px;
}

.table-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.quote-template-select {
  width: 280px;
}

.quote-template-option {
  float: right;
  color: #909399;
  font-size: 12px;
}

.product-cell {
  display: flex;
  flex-direction: column;
  line-height: 18px;
}

.product-cell span {
  color: #909399;
  font-size: 12px;
}

.quote-no-cell {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
}

.quote-no-link {
  font-family: Consolas, Monaco, monospace;
  white-space: nowrap;
}

.quote-no-cell .el-button {
  margin-left: 6px;
  padding: 0;
  font-size: 14px;
  flex: 0 0 auto;
}

.summary-panel {
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
  margin-top: 12px;
  padding: 12px;
  background: #f8fafc;
  border: 1px solid #e6ebf2;
  border-radius: 4px;
}

.risk-tag {
  margin: 2px;
}

.danger {
  color: #f56c6c;
}

.quote-sheet-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 12px;
}

.quote-sheet-scroll {
  max-height: calc(100vh - 210px);
  overflow: auto;
  background: #eef2f7;
  padding: 16px;
}

.seal-approval-alert {
  margin-bottom: 14px;
}

.quote-sheet-document {
  width: 980px;
  margin: 0 auto;
  padding: 28px 32px;
  background: #fff;
  color: #1f2933;
  box-shadow: 0 2px 10px rgba(31, 41, 51, 0.08);
}

.quote-sheet-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  border-bottom: 2px solid #1f2933;
  padding-bottom: 18px;
  margin-bottom: 18px;
}

.quote-sheet-header h1 {
  margin: 0 0 8px;
  font-size: 28px;
  letter-spacing: 0;
  font-weight: 700;
}

.quote-sheet-header p {
  margin: 0;
  color: #5f6b7a;
  font-size: 14px;
}

.quote-sheet-no {
  text-align: right;
}

.quote-sheet-no span {
  display: block;
  color: #6b7280;
  font-size: 13px;
  margin-bottom: 6px;
}

.quote-sheet-no strong {
  font-size: 18px;
}

.quote-sheet-meta {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border: 1px solid #d8dee8;
  border-bottom: 0;
  margin-bottom: 18px;
}

.quote-sheet-meta div {
  padding: 10px 12px;
  border-right: 1px solid #d8dee8;
  border-bottom: 1px solid #d8dee8;
  min-height: 44px;
}

.quote-sheet-meta div:nth-child(3n) {
  border-right: 0;
}

.quote-sheet-meta span {
  display: block;
  color: #6b7280;
  font-size: 12px;
  margin-bottom: 4px;
}

.quote-sheet-meta strong {
  font-size: 14px;
  font-weight: 600;
}

.quote-sheet-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.quote-sheet-table th {
  background: #f0f3f8;
  color: #1f2933;
  border: 1px solid #cfd7e3;
  padding: 9px 8px;
  text-align: left;
}

.quote-sheet-table td {
  border: 1px solid #d8dee8;
  padding: 8px;
  vertical-align: top;
  line-height: 1.45;
}

.quote-sheet-table small {
  display: block;
  color: #6b7280;
  margin-top: 4px;
}

.quote-sheet-table .center {
  text-align: center;
}

.quote-sheet-table .number {
  text-align: right;
  white-space: nowrap;
}

.quote-sheet-table .empty {
  color: #909399;
  padding: 24px;
}

.quote-sheet-table--equipment th:nth-child(1) {
  width: 52px;
}

.quote-sheet-table--equipment th:nth-child(2) {
  width: 150px;
}

.quote-sheet-table--equipment th:nth-child(4),
.quote-sheet-table--equipment th:nth-child(5) {
  width: 64px;
}

.quote-sheet-table--equipment th:nth-child(6),
.quote-sheet-table--equipment th:nth-child(7) {
  width: 92px;
}

.equipment-group-row td {
  background: #eef3fb;
  font-weight: 700;
}

.equipment-subtotal-row td {
  background: #f8fafc;
  font-weight: 700;
}

.quote-sheet-total {
  width: 360px;
  margin: 18px 0 0 auto;
  border: 1px solid #d8dee8;
}

.quote-sheet-total div {
  display: flex;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid #d8dee8;
}

.quote-sheet-total div:last-child {
  border-bottom: 0;
}

.quote-sheet-total .grand-total {
  background: #f8fafc;
  font-size: 16px;
}

.quote-sheet-remark {
  margin-top: 18px;
  border: 1px solid #d8dee8;
  padding: 12px;
}

.quote-sheet-remark span {
  display: block;
  color: #6b7280;
  font-size: 12px;
  margin-bottom: 6px;
}

.quote-sheet-remark p {
  margin: 0;
  white-space: pre-wrap;
  line-height: 1.6;
}

.quote-item-table ::v-deep .el-input-number--mini {
  width: 104px;
}
</style>
